import React, { useMemo, useEffect } from "react";
import { Calendar, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useGetCurrentYearHolidaysQuery } from "../../store/apis/holidayManagement.api";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";

interface HolidayCardProps {
  /** Maximum number of holidays to display. If undefined, all upcoming holidays are displayed. */
  limit?: number;
  /** Max height class for the list (default: 'max-h-[220px]'). */
  maxHeight?: string;
  /** Whether the list should allow vertical scrolling. Default: true. */
  scrollable?: boolean;
  /** Optional container class name. */
  className?: string;
}

const HolidayCard: React.FC<HolidayCardProps> = ({
  limit,
  maxHeight = "max-h-[220px]",
  scrollable = true,
  className = "",
}) => {
  const canAccessHolidayManagement = useHasPermission([
    PERMISSIONS.HOLIDAY_MANAGEMENT_VIEW,
    PERMISSIONS.HOLIDAY_MANAGEMENT_MANAGE,
  ]);

  const { data: holidays = [], isLoading, isError, error } =
    useGetCurrentYearHolidaysQuery();

  const upcomingHolidays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sorted = [...holidays]
      .filter((h) => new Date(h.date) >= today)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const items = limit ? sorted.slice(0, limit) : sorted;

    return items.map((h) => {
      const d = new Date(h.date);
      const day = d.getDate().toString().padStart(2, "0");
      const mon = d.toLocaleDateString("en-US", { month: "short" });
      const weekday = d.toLocaleDateString("en-US", { weekday: "long" });
      return { ...h, day, mon, weekday };
    });
  }, [holidays, limit]);

  useEffect(() => {
    const bridge = (
      globalThis as typeof globalThis & {
        ReactNativeWebView?: { postMessage: (m: string) => void };
      }
    ).ReactNativeWebView;
    if (bridge && upcomingHolidays.length > 0) {
      bridge.postMessage(
        JSON.stringify({ type: "HOLIDAY_LIST", payload: upcomingHolidays })
      );
    }
  }, [upcomingHolidays]);

  if (isLoading) {
    return (
      <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft min-h-[200px] h-full flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (isError) {
    const message =
      error && typeof error === "object" && "data" in error
        ? (error.data as { message?: string })?.message
        : "Failed to load holidays";
    return (
      <div className="bg-surface border border-border-secondary rounded-card p-5 shadow-soft min-h-[200px] h-full flex flex-col items-center justify-center">
        <p className="text-body-sm font-medium text-error">{message}</p>
      </div>
    );
  }

  return (
    <div className={`bg-surface border border-border-secondary rounded-card p-4 sm:p-5 shadow-soft h-auto lg:h-full flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <div className="flex items-center gap-2">
          <Calendar className="w-4.5 h-4.5 text-primary-600" />
          <h3 className="text-heading-sm font-bold text-text-primary leading-tight">
            Upcoming Holidays
          </h3>
        </div>
        {canAccessHolidayManagement && (
          <Link
            to="/admin/holiday-management"
            className="text-body-sm font-bold text-primary-700 hover:text-primary-800 transition-colors flex items-center gap-1 no-underline"
          >
            View all
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>

      {/* Holiday List */}
      <div className={`flex-1 min-h-0 ${maxHeight} ${scrollable ? "overflow-y-auto scrollbar-thin pr-1" : "overflow-hidden"}`}>
        {upcomingHolidays.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <Calendar className="w-8 h-8 text-text-tertiary mb-2" />
            <p className="text-body-sm font-bold text-text-primary">
              No upcoming holidays
            </p>
            <p className="text-body-xs text-text-tertiary mt-1">
              Holidays will appear here
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {upcomingHolidays.map((h) => (
              <div
                key={h.id ?? h.date}
                className="flex items-center gap-[13px] py-1.5 px-1 rounded-xl hover:bg-layout-hover transition-colors"
              >
                <div className="w-[44px] flex-none text-center bg-event-holiday-bg rounded-avatar py-1 shadow-2xs">
                  <div className="text-[17px] font-extrabold text-event-holiday leading-none">
                    {h.day}
                  </div>
                  <div className="text-[10px] font-bold text-event-holiday-sub uppercase tracking-wider mt-0.5">
                    {h.mon}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-subtitle font-bold text-text-primary truncate">
                    {h.name}
                  </div>
                  <div className="text-body-xs font-normal text-text-tertiary mt-0.5">
                    {h.weekday}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {h.isRecurring && (
                    <span className="text-caption font-bold text-primary-700 bg-primary-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                      Recurring
                    </span>
                  )}
                  <span className="text-caption font-bold text-text-secondary bg-layout-muted px-2.5 py-0.5 rounded-full whitespace-nowrap">
                    {h.isMandatory ? "Mandatory" : "Optional"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HolidayCard;
